'use client'

import React from 'react'
import axios from 'axios'
import { ExternalLink, Loader2 } from 'lucide-react'
import { useSWRConfig } from 'swr'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import { ToastAction } from '@/components/ui/toast'
import { cn } from '@/lib/utils'
import { API_URL, ESPO_URL } from '@/utils/constants'
import Wallet from '@/utils/wallet'
import useLoggedIn from '@/utils/hooks/useLoggedIn'
import useDebounce from '@/utils/hooks/useDebounce'
import useFeeEstimates, { useSelectFeeRate } from '@/utils/hooks/useFeeEstimates'
import useAlkanesOutpoints from '@/utils/hooks/useAlkanesOutpoints'
import event from '@/utils/event'
import {
	applySlippage,
	bestQuote,
	bestQuoteExactOut,
	BTC_ID,
	formatAmount,
	frbtcMidPriceScaled,
	FRBTC_ID,
	FRUSD_ID,
	parseAmount,
	hopFeePct,
	ROUTER_ENABLED,
	SFAMM_VIA_ROUTER,
	wrapIn,
	wrapOut,
	type PoolToken,
	type Quote
} from '../lib/amm'
import { buyFunding, topupForSwapInput } from '../lib/funding'
import useCurveQuote from '../lib/useCurveQuote'
import { formatPrice, numCompact, PRICE_SCALE } from '../lib/format'
import { formatUsd, impactCostUsd, maxInput, quoteMoved } from '../lib/review'
import { recordSwap } from '../lib/history'
import { useAlkanes } from '../lib/store'
import HoverDetail from './hover-detail'
import { TokenIcon } from './token-select'

const HIGH_IMPACT_PCT = 5
/// Above this the pool cannot serve the order in any meaningful sense — the
/// quote is arithmetic, not a price. Presenting 99.99% impact and a
/// $62,538,204,608,789 cost in the same calm type as a $1 trade is the
/// interface agreeing with a nonsense request.
const UNUSABLE_IMPACT_PCT = 50
/// A PSBT built for the confirm block is signed as-is inside this window;
/// past it we rebuild so the signature never covers a stale UTXO set.
const PREP_TTL_MS = 120_000
/// How often the pending-wrap banner re-checks the wallet for frBTC arrival.
const WRAP_POLL_MS = 30_000

/// What pressing the button actually does, derived from the pair:
/// - wrap:        BTC → frBTC via the signer (0.1% fee)
/// - wrapSwap:    BTC → token in ONE action: a wrap and a swap, built and
///                broadcast together, confirming in the same block
/// - unwrap:      frBTC → BTC via the signer (async settlement)
/// - swapToFrbtc: token → frBTC now; unwrapping to BTC is a manual second step
/// - swap:        plain AMM swap
type Mode = 'swap' | 'wrap' | 'wrapSwap' | 'unwrap' | 'swapToFrbtc' | 'curve'

function modeOf(sellId: string, buyId: string): Mode {
	// frBTC ⇄ frUSD trades on the CryptoSwap pool, never the AMM: the AMM's
	// deepest frUSD pool holds tens of dollars and would quote fiction.
	if ((sellId === FRBTC_ID && buyId === FRUSD_ID) || (sellId === FRUSD_ID && buyId === FRBTC_ID)) {
		return 'curve'
	}
	if (sellId === BTC_ID) {
		return buyId === FRBTC_ID ? 'wrap' : 'wrapSwap'
	}
	if (buyId === BTC_ID) {
		return sellId === FRBTC_ID ? 'unwrap' : 'swapToFrbtc'
	}
	return 'swap'
}

/// swapToFrbtc stops at frBTC: the unwrap to BTC is a separate transaction
/// the user starts later, and everything the mode renders must say so.
/// (wrapSwap used to stop there too — it is now a chained one-action trade.)
function isTwoStep(mode: Mode): boolean {
	return mode === 'swapToFrbtc'
}

interface Plan {
	mode: Mode
	/// Sell-side raw units (sats when selling BTC). For a topped-up wrapSwap
	/// this is the TOTAL BTC-side commitment: wrapped sats + carried frBTC
	/// (1:1 sats), which is what the sell field honestly displays.
	amountIn: bigint
	/// Raw units the ACTION delivers — for wrapSwap that is the bought token,
	/// since both transactions broadcast together.
	amountOut: bigint
	/// The AMM leg inside this action, when the mode has one. For wrapSwap it
	/// is the chained second transaction's quote on the minted frBTC (plus
	/// the carried frBTC when topping up).
	swapQuote: Quote | null
	/// Curve mode only: the quote's output at the pool's spot marginal —
	/// impact renders as (1 − amountOut/spot).
	curveSpotOut?: bigint | null
	/// Route for display, covering the whole action.
	displayPath: string[]
	/// wrapSwap only — the partial top-up: wrap `wrapSats` (the shortfall)
	/// and let `carryFrbtc` of held frBTC ride the swap leg. Absent = the
	/// full amount wraps, exactly as before.
	topup?: { wrapSats: bigint; carryFrbtc: bigint }
}

/// A frozen copy of exactly what the confirm block asks the user to approve.
/// Signing reads from here and never from the live quote, so the transaction
/// cannot differ from the numbers that were on screen.
interface Review {
	plan: Plan
	sell: PoolToken
	buy: PoolToken
	/// The token this transaction actually produces.
	outToken: PoolToken
	verb: string
	minOut: bigint
	maxIn: bigint
	slippage: number
	feeRate: number
	carriers: [string, number][] | null
	/// The frozen top-up package: the held frBTC carrier outpoints the swap
	/// leg spends, alongside the split the plan decided. Null = full wrap.
	topup: { wrapSats: bigint; carryFrbtc: bigint; outpoints: [string, number][] } | null
	impactPct: number
	impactUsd: number | null
	loud: boolean
}

/// The chained wrap-swap package: two PSBTs whose signed txids must equal
/// the pre-sign ids before either broadcasts.
interface ChainBuilt {
	wrapPsbt: string
	swapPsbt: string
	wrapTxid: string
	swapTxid: string
	frbtcMinted: bigint
	/// What the swap protostone actually sells: the mint, plus the carried
	/// frBTC when topping up. Verified against the plan before signing.
	swapAmountIn: bigint
}

/// Thrown (by message) when the API failed the top-up capability echo — an
/// older /alkane/wrap-swap silently ignores `frbtc_outpoints` and its chain
/// would wrap only the shortfall while the quote priced the combined amount.
/// The panel catches this, downgrades the session to full-wrap plans, and
/// asks for one more review click — nothing is ever signed on this path.
const TOPUP_UNSUPPORTED = 'wrap-swap-topup-unsupported'

/// Network fee for the reviewed trade, taken from the PSBT(s) the API builds
/// rather than guessed from a vsize the client cannot know. For wrapSwap the
/// fee is BOTH transactions' fees and `chain` carries the package.
type Prep =
	| { status: 'idle' }
	| { status: 'building' }
	| { status: 'ready'; psbt: string; fee: bigint; at: number; chain?: ChainBuilt }
	| { status: 'failed' }

/// The quoted route's pools in the wire shape the API takes — one
/// `[poolId, outSlot]` per hop, which is what makes the swap go through
/// OWRouter instead of the AMM factory. Taken from the SAME quote the review
/// block priced, so the signed route cannot differ from the shown one.
function routePools(q: Quote): [string, boolean][] {
	// Switch off ⇒ send nothing, which the API reads as "use the AMM factory".
	// Sending pools while the proxy still runs an older implementation would
	// have the router misread the calldata, so the flag gates the WIRE, not
	// just the arithmetic.
	if (!ROUTER_ENABLED) {
		return []
	}
	return q.pools.map((p): [string, boolean] => [p.poolId, p.outSlot])
}

async function buildPsbt(r: Review): Promise<{ psbt: string; fee: bigint; chain?: ChainBuilt }> {
	if (r.plan.mode === 'wrapSwap') {
		const t = r.topup
		// u64 sats — always well inside Number range.
		const c = await Wallet.getAlkaneWrapSwap(
			Number(t ? t.wrapSats : r.plan.amountIn),
			r.plan.swapQuote!.path,
			r.minOut,
			r.feeRate,
			routePools(r.plan.swapQuote!),
			t ? { outpoints: t.outpoints, carriedFrbtc: t.carryFrbtc } : null
		)
		if (t) {
			// The capability echo: the top-up-aware API reports the exact
			// amount its swap protostone sells. Anything else means the server
			// ignored the carriers and this chain would sell LESS than the
			// reviewed quote — refuse it before a signature can exist.
			const expected = wrapOut(t.wrapSats) + t.carryFrbtc
			if (c.swapAmountIn === null || c.swapAmountIn !== expected) {
				throw new Error(TOPUP_UNSUPPORTED)
			}
		}
		return {
			psbt: c.wrapPsbt,
			fee: c.wrapFee + c.swapFee,
			chain: {
				wrapPsbt: c.wrapPsbt,
				swapPsbt: c.swapPsbt,
				wrapTxid: c.wrapTxid,
				swapTxid: c.swapTxid,
				frbtcMinted: c.frbtcMinted,
				swapAmountIn: c.swapAmountIn ?? c.frbtcMinted
			}
		}
	}
	if (r.plan.mode === 'wrap') {
		return Wallet.getAlkaneWrap(Number(r.plan.amountIn), r.feeRate)
	}
	if (r.plan.mode === 'unwrap') {
		return Wallet.getAlkaneUnwrap(r.plan.amountIn, r.carriers!, r.feeRate)
	}
	if (r.plan.mode === 'curve') {
		return Wallet.getAlkaneCurveSwap(r.sell.id, r.plan.amountIn, r.minOut, r.carriers!, r.feeRate)
	}
	return Wallet.getAlkaneSwap(
		r.plan.swapQuote!.path,
		r.plan.amountIn,
		r.minOut,
		r.carriers!,
		r.feeRate,
		routePools(r.plan.swapQuote!)
	)
}

/// "1%", "1.5%" — a pool fee percentage without trailing zeros.
const pctLabel = (pct: number) => `${Number(pct.toFixed(2))}%`

/// Short venue name for fee rows.
const venueName = (venue: string) => (venue === 'subfrostamm' ? 'SUBFROST' : 'Oyl')

/// The router's take as a percentage string, from the same live value the quote
/// math subtracts — the row and the number can never drift apart.
const routerFeePct = (feePer1000: bigint) => `${Number(feePer1000) / 10}%`

/// One reserved slot of the inline invoice: fixed label, streaming value.
function InvoiceRow({
	label,
	testId,
	children
}: {
	label: string
	testId?: string
	children: React.ReactNode
}) {
	return (
		<div
			className="flex min-w-0 items-center justify-between gap-2 whitespace-nowrap"
			data-testid={testId}
		>
			<dt className="shrink-0 text-muted-foreground">{label}</dt>
			<dd className="min-w-0 text-right tabular-nums">{children}</dd>
		</div>
	)
}

/// The honest unknown, in every reserved slot the quote has not filled.
const dash = <span className="text-muted-foreground">—</span>

function impactTone(pct: number): string {
	if (pct >= 5) {
		return 'text-destructive-ink'
	}
	if (pct >= 1.5) {
		return 'text-warning-ink'
	}
	return 'text-muted-foreground'
}

const plain = (s: string) => s.replace(/,/g, '')

/// What a successful broadcast was, for a caller that renders its own
/// completion surface (the trade sheet's full-screen "Bought" state). Purely
/// informational — by the time this fires the transaction is already on the
/// network.
export interface BroadcastInfo {
	kind: 'swap' | 'wrap' | 'unwrap'
	txid: string
	sellId: string
	sellName: string
	outId: string
	outName: string
	amountInRaw: string
	amountOutRaw: string
}

/// The trade — the audited machinery behind every buy and sell, rendered as
/// the INLINE INVOICE of its host surface rather than a module of its own.
///
/// Everything from the props line to the JSX is the audited flow, unchanged:
/// the quote, the frozen Review snapshot that signing reads from, the
/// real-PSBT fee, the impact guards and the auto-slippage. The presentation
/// changed shape (2026-08-10): the editable two-box module is gone — the host
/// (phone sheet / desktop box) owns the amount and feeds it in live, and this
/// component renders the invoice that materializes beneath it, the single
/// CTA, and the frozen confirm stage. First click still never signs.
export default function SwapPanel({
	sellAmount,
	buyAmount,
	reviewLabel,
	reviewRequest,
	footClassName,
	onBroadcast,
	onReviewChange,
	staged = false
}: {
	/// LIVE sell-side amount (ungrouped decimal string) from the host's entry.
	/// The amount flows through exactly the same quote, guards and review a
	/// typed one always did — a front door, not a bypass.
	sellAmount?: string
	/// Live buy-side amount instead (exact-out entry).
	buyAmount?: string
	/// The ready-state CTA label ("Review buy"). Every other state keeps the
	/// engine's honest ladder (connect / insufficient / no route / …).
	reviewLabel?: string
	/// Bump to ask for the review stage as soon as the plan can freeze —
	/// instant-trade intents use this instead of an imperative handle. An
	/// amount edit after the bump cancels it.
	reviewRequest?: number
	/// Classes for the CTA + caption block. A scrolling host (the phone
	/// sheet) pins it sticky to its foot so the CTA NEVER moves — not on the
	/// invoice reveal, not on quote arrival, not on a short viewport.
	footClassName?: string
	/// When provided, a successful broadcast reports here INSTEAD of raising
	/// the success toast, so a wrapping surface can own the completion screen.
	/// Failures still toast — errors are never anyone else's to swallow.
	onBroadcast?: (info: BroadcastInfo) => void
	/// Fires when the frozen review stage opens or closes, so the host can
	/// collapse its entry chrome (numpad, chips) into the confirm stage.
	onReviewChange?: (open: boolean) => void
	/// Two explicit steps (the phone sheet): the entry stage carries NO
	/// invoice — its CTA freezes the review — and the invoice mounts only
	/// with the review stage, whose CTA signs. A content-sized bottom sheet
	/// therefore never changes height while the user types; it changes on
	/// the tap that asks it to. Desktop keeps the streaming inline invoice.
	staged?: boolean
}) {
	const pools = useAlkanes((s) => s.pools)
	/// Chain tip, for the pending-wrap banner.
	const tipHeight = useAlkanes((s) => s.tipHeight)
	/// OWRouter's fee per 1000, from the same pools payload as the reserves. It
	/// is contract state (op-21 `set_fee`), so it arrives with every quote
	/// rather than being compiled in — a stale fee quotes a `min_out` the router
	/// cannot pay and the route refunds instead of filling.
	const routerFeePer1000 = useAlkanes((s) => s.routerFeePer1000)
	/// SUBFROSTAMM pools: a second venue the route engine weighs hop by hop,
	/// through the router only — dormant until `SFAMM_VIA_ROUTER` (router v2.5).
	const sfammPools = useAlkanes((s) => s.sfammPools)
	const routeOpts = React.useMemo(
		() => ({ sfammPools, sfammViaRouter: SFAMM_VIA_ROUTER }),
		[sfammPools]
	)
	/// The pool registry never answered, so there are no reserves to quote
	/// against. The submit control may not imply otherwise.
	const marketDataDown = useAlkanes((s) => s.health) === 'down'
	/// Whether the API can mix held frBTC into a wrap-swap (the top-up rung).
	/// Assumed until a build fails the `swap_amount_in` echo — then the whole
	/// session re-plans as full wraps and the copy stops promising a top-up.
	const wrapSwapTopup = useAlkanes((s) => s.wrapSwapTopup)
	const markWrapSwapTopupUnsupported = useAlkanes((s) => s.markWrapSwapTopupUnsupported)
	const tokens = useAlkanes((s) => s.tokens)
	const sell = useAlkanes((s) => s.sell)
	const buy = useAlkanes((s) => s.buy)
	const balances = useAlkanes((s) => s.balances)
	const balancesUnavailable = useAlkanes((s) => s.balancesUnavailable)
	const btcUsd = useAlkanes((s) => s.btcUsd)
	const watchAddress = useAlkanes((s) => s.watchAddress)
	const watchMode = watchAddress !== null
	const onSelectSell = useAlkanes((s) => s.selectSell)
	const onSelectBuy = useAlkanes((s) => s.selectBuy)
	const onSwapped = useAlkanes((s) => s.refreshWallet)

	// USD value of a raw amount via its deepest frBTC route (frBTC ≈ BTC), and
	// the frBTC-side depth behind a name. Both are pure functions of the pool
	// set, so they are derived here rather than stored — a function in the
	// store would be one more thing that has to stay in sync with the reserves
	// it closes over.
	const usdOf = React.useCallback(
		(id: string, raw: bigint): number | null => {
			if (btcUsd === null) {
				return null
			}
			if (id === BTC_ID) {
				return (Number(raw) / 1e8) * btcUsd
			}
			const mid = frbtcMidPriceScaled(pools, id)
			return mid === null ? null : (Number(raw * mid) / 1e24) * btcUsd
		},
		[pools, btcUsd]
	)
	const { toast } = useToast()
	const { mutate } = useSWRConfig()
	const loggedIn = useLoggedIn()
	// The SITE-WIDE fee preference — the same slow/medium/fast level the
	// status bar's gear and the rest of the wallet read and write
	// (`ordinals_wallet::selected_fee_level`). One preference, every builder:
	// the rate below is the `fee_rate` the swap/wrap/unwrap requests carry.
	const { speed, selected } = useFeeEstimates()
	const selectFee = useSelectFeeRate()
	// Amount state is always UNGROUPED — `parseAmount` is the source of truth
	// and it does not accept separators. The host's entry is the only writer;
	// `plain` guards against a grouped string ever arriving.
	const [sellInput, setSellInput] = React.useState(() => plain(sellAmount ?? ''))
	const [buyInput, setBuyInput] = React.useState(() => plain(buyAmount ?? ''))
	// Exact-in vs exact-out: whichever side the host feeds is the source of
	// truth; the other one is derived from the quote. A buy-side amount IS an
	// exact-out entry, so it claims the truth.
	const [lastEdited, setLastEdited] = React.useState<'sell' | 'buy'>(() =>
		buyAmount && !sellAmount ? 'buy' : 'sell'
	)
	const [submitting, setSubmitting] = React.useState(false)
	// The confirmation surface: the first click reviews, the second signs.
	const [review, setReview] = React.useState<Review | null>(null)
	const [prep, setPrep] = React.useState<Prep>({ status: 'idle' })
	const prepSeq = React.useRef(0)
	// A broadcast wrap keeps the panel mounted and reports its own progress
	// instead of replacing the trading surface with a dead end.
	const [pendingWrap, setPendingWrap] = React.useState<{
		txid: string
		minted: bigint
		intent: PoolToken | null
		atHeight: number | null
		frbtcBefore: bigint
	} | null>(null)

	const address = watchAddress ?? (loggedIn ? Wallet.getTaprootAddress() : undefined)
	// Account switched: the pending wrap and any open review were the OLD
	// account's — its mempool banner must not follow the user to the new one.
	const [panelFor, setPanelFor] = React.useState(address)
	if (panelFor !== address) {
		setPanelFor(address)
		setPendingWrap(null)
		setReview(null)
		setPrep({ status: 'idle' })
	}
	// Reads behave logged-in under watch mode; only signing is gated.
	const canRead = loggedIn || watchMode
	// 'undefined' short-circuits the hook's fetcher — BTC has no alkane
	// outpoints. A wrapSwap pair (selling BTC into a token) fetches the frBTC
	// carriers instead: they are what a top-up rides on the swap leg.
	const wantsWrapSwap = sell?.id === BTC_ID && buy !== null && buy.id !== FRBTC_ID
	const outpointsId = sell && sell.id !== BTC_ID ? sell.id : wantsWrapSwap ? FRBTC_ID : 'undefined'
	const { data: sellOutpoints, error: sellOutpointsError } = useAlkanesOutpoints(
		outpointsId,
		address
	)
	const carrierDataUnavailable = Boolean(
		canRead && outpointsId !== 'undefined' && sellOutpointsError !== undefined
	)

	/// Held frBTC that can actually ride a wrap-swap: the sum carried by
	/// usable (non-escrow) outpoints. Deliberately NOT the balance figure —
	/// the swap leg spends real carriers, so the decision must be funded by
	/// the same set the transaction will name.
	const frbtcCarriable = React.useMemo(() => {
		if (!wantsWrapSwap || !Array.isArray(sellOutpoints)) {
			return 0n
		}
		let total = 0n
		for (const o of sellOutpoints) {
			if (o.escrow) {
				continue
			}
			const raw = parseAmount(String(o.amount))
			if (raw && raw > 0n) {
				total += raw
			}
		}
		return total
	}, [wantsWrapSwap, sellOutpoints])

	// ── curve quotes (frBTC ⇄ frUSD) ────────────────────────────────────────
	// The pool's get_dy is server-side, so curve quotes are async where AMM
	// quotes are pool-math. Exact-in keys the hook on the typed amount;
	// exact-out first learns the spot from a small probe, sizes the input at
	// spot plus a 0.5% pad, and ships THAT exact-in — the invoice shows the
	// real quote for it, and min_out still guards the fill.
	const curvePair = Boolean(
		sell &&
			buy &&
			((sell.id === FRBTC_ID && buy.id === FRUSD_ID) ||
				(sell.id === FRUSD_ID && buy.id === FRBTC_ID))
	)
	const curveTypedIn = curvePair && lastEdited === 'sell' ? parseAmount(sellInput) : null
	const curveWant = curvePair && lastEdited === 'buy' ? parseAmount(buyInput) : null
	const curveProbe = useCurveQuote(
		curvePair && curveWant !== null && curveWant > 0n ? sell!.id : null,
		100_000n
	)
	const curveEstimatedIn =
		curveWant !== null && curveWant > 0n && curveProbe && curveProbe.amountOut > 0n
			? (curveWant * curveProbe.amountIn * 1005n) / (curveProbe.amountOut * 1000n)
			: null
	const curveQuote = useCurveQuote(
		curvePair ? sell!.id : null,
		lastEdited === 'sell' ? curveTypedIn : curveEstimatedIn
	)

	const plan: Plan | null = React.useMemo(() => {
		if (!sell || !buy || sell.id === buy.id) {
			return null
		}
		const mode = modeOf(sell.id, buy.id)
		if (lastEdited === 'sell') {
			const amtIn = parseAmount(sellInput)
			if (!amtIn || amtIn <= 0n) {
				return null
			}
			switch (mode) {
				case 'wrap':
				case 'unwrap': {
					const out = wrapOut(amtIn)
					if (out <= 0n) {
						return null
					}
					const path = mode === 'wrap' ? [BTC_ID, FRBTC_ID] : [FRBTC_ID, BTC_ID]
					return {
						mode,
						amountIn: amtIn,
						amountOut: out,
						swapQuote: null,
						displayPath: path
					}
				}
				case 'wrapSwap': {
					// One action, two chained transactions: the wrap mints frBTC
					// and the swap sells ALL of it in the same block — so the
					// quote on the minted amount IS this action's output.
					//
					// The third rung: when held frBTC is positive but short, the
					// funding module decides a top-up — only the shortfall wraps
					// and the held carriers ride the swap leg, so the quote runs
					// on mint + carried. Gated on `wrapSwapTopup` (the session
					// capability flag) so an older API never sees a
					// shortfall-sized wrap it would under-fund.
					const f = buyFunding(frbtcCarriable, balances.get(BTC_ID) ?? 0n, amtIn, wrapSwapTopup)
					if (f.source === 'topup') {
						const swapIn = wrapOut(f.wrapSats!) + f.carryFrbtc!
						const q = bestQuote(pools, FRBTC_ID, buy.id, swapIn, routerFeePer1000, routeOpts)
						if (!q) {
							return null
						}
						return {
							mode,
							amountIn: amtIn,
							amountOut: q.amountOut,
							swapQuote: q,
							displayPath: [BTC_ID, ...q.path],
							topup: { wrapSats: f.wrapSats!, carryFrbtc: f.carryFrbtc! }
						}
					}
					const net = wrapOut(amtIn)
					if (net <= 0n) {
						return null
					}
					const q = bestQuote(pools, FRBTC_ID, buy.id, net, routerFeePer1000, routeOpts)
					if (!q) {
						return null
					}
					return {
						mode,
						amountIn: amtIn,
						amountOut: q.amountOut,
						swapQuote: q,
						displayPath: [BTC_ID, ...q.path]
					}
				}
				case 'swapToFrbtc': {
					const q = bestQuote(pools, sell.id, FRBTC_ID, amtIn, routerFeePer1000, routeOpts)
					if (!q) {
						return null
					}
					return {
						mode,
						amountIn: amtIn,
						amountOut: q.amountOut,
						swapQuote: q,
						displayPath: q.path
					}
				}
				case 'curve': {
					if (!curveQuote || curveQuote.sell !== sell.id || curveQuote.amountIn !== amtIn) {
						return null
					}
					return {
						mode,
						amountIn: amtIn,
						amountOut: curveQuote.amountOut,
						swapQuote: null,
						curveSpotOut: curveQuote.spotOut,
						displayPath: [sell.id, buy.id]
					}
				}
				case 'swap': {
					const q = bestQuote(pools, sell.id, buy.id, amtIn, routerFeePer1000, routeOpts)
					if (!q) {
						return null
					}
					return {
						mode,
						amountIn: amtIn,
						amountOut: q.amountOut,
						swapQuote: q,
						displayPath: q.path
					}
				}
			}
		}
		// Exact-out: invert along the best route, then execution stays exact-in.
		const want = parseAmount(buyInput)
		if (!want || want <= 0n) {
			return null
		}
		switch (mode) {
			case 'wrap':
			case 'unwrap': {
				const amtIn = wrapIn(want)
				const path = mode === 'wrap' ? [BTC_ID, FRBTC_ID] : [FRBTC_ID, BTC_ID]
				return {
					mode,
					amountIn: amtIn,
					amountOut: want,
					swapQuote: null,
					displayPath: path
				}
			}
			case 'wrapSwap': {
				// Exact-out on the TOKEN: invert the swap to the frBTC it needs,
				// then invert the wrap to the BTC that mints that frBTC. With
				// held frBTC and a top-up-capable API, part of that input rides
				// as-is and only the remainder's mint is inverted to sats.
				const q = bestQuoteExactOut(pools, FRBTC_ID, buy.id, want, routerFeePer1000, routeOpts)
				if (!q) {
					return null
				}
				const t = topupForSwapInput(
					frbtcCarriable,
					balances.get(BTC_ID) ?? 0n,
					q.amountIn,
					wrapSwapTopup
				)
				if (t) {
					return {
						mode,
						// Total BTC-side commitment: wrapped sats + carried frBTC.
						amountIn: t.wrapSats + t.carryFrbtc,
						amountOut: q.amountOut,
						swapQuote: q,
						displayPath: [BTC_ID, ...q.path],
						topup: t
					}
				}
				return {
					mode,
					amountIn: wrapIn(q.amountIn),
					amountOut: q.amountOut,
					swapQuote: q,
					displayPath: [BTC_ID, ...q.path]
				}
			}
			case 'swapToFrbtc': {
				const q = bestQuoteExactOut(pools, sell.id, FRBTC_ID, want, routerFeePer1000, routeOpts)
				if (!q) {
					return null
				}
				return {
					mode,
					amountIn: q.amountIn,
					amountOut: want,
					swapQuote: q,
					displayPath: q.path
				}
			}
			case 'curve': {
				// The spot-sized estimate became the hook's key; its REAL quote
				// is what this plan promises. amountOut ≈ want by construction.
				if (!curveQuote || curveQuote.sell !== sell.id) {
					return null
				}
				return {
					mode,
					amountIn: curveQuote.amountIn,
					amountOut: curveQuote.amountOut,
					swapQuote: null,
					curveSpotOut: curveQuote.spotOut,
					displayPath: [sell.id, buy.id]
				}
			}
			case 'swap': {
				const q = bestQuoteExactOut(pools, sell.id, buy.id, want, routerFeePer1000, routeOpts)
				if (!q) {
					return null
				}
				return {
					mode,
					amountIn: q.amountIn,
					amountOut: want,
					swapQuote: q,
					displayPath: q.path
				}
			}
		}
	}, [
		pools,
		routerFeePer1000,
		routeOpts,
		sell,
		buy,
		sellInput,
		buyInput,
		lastEdited,
		frbtcCarriable,
		balances,
		wrapSwapTopup,
		curveQuote
	])

	const mode = plan?.mode ?? (sell && buy ? modeOf(sell.id, buy.id) : 'swap')
	const twoStep = isTwoStep(mode)
	const frbtcToken = React.useMemo(
		(): PoolToken =>
			tokens.find((t) => t.id === FRBTC_ID) ?? { id: FRBTC_ID, name: 'frBTC', icon: null },
		[tokens]
	)
	/// What the Buy field is allowed to promise: this transaction's output.
	const outToken = twoStep ? frbtcToken : buy
	const sellBalance = sell ? balances.get(sell.id) ?? 0n : 0n
	// The sell-side amount to validate against: the typed amount when editing
	// the sell field, else the exact-out computed input.
	const sellSideAmount =
		lastEdited === 'sell' ? parseAmount(sellInput) : plan ? plan.amountIn : null
	// Only meaningful once a wallet is connected — a visitor typing an amount
	// shouldn't be shouted at in red before they can even hold a balance.
	// A top-up plan only draws its wrapped shortfall from the BTC balance —
	// the carried part is already funded by the frBTC carriers it named.
	const insufficient =
		canRead &&
		!balancesUnavailable &&
		sellSideAmount !== null &&
		(plan?.topup ? plan.topup.wrapSats > sellBalance : sellSideAmount > sellBalance)
	const typedAmount = lastEdited === 'sell' ? parseAmount(sellInput) : parseAmount(buyInput)
	const hasAmount = typedAmount !== null && typedAmount > 0n
	const noRoute = Boolean(sell && buy && hasAmount && !plan)
	// The action's impact — the number the user steers by now that slippage
	// is automatic.
	const impactQuote = plan?.swapQuote ?? null
	const impactOutId = plan?.swapQuote ? outToken?.id ?? null : null
	// Curve impact is (1 − out/spot) from the quote's own spot leg; AMM
	// impact comes with the quote. Same number, same guards downstream.
	const curveImpactPct =
		plan?.mode === 'curve' && plan.curveSpotOut && plan.curveSpotOut > 0n
			? Math.max(0, (1 - Number(plan.amountOut) / Number(plan.curveSpotOut)) * 100)
			: null
	const impactPct = curveImpactPct ?? impactQuote?.priceImpactPct ?? 0
	const highImpact = impactPct > HIGH_IMPACT_PCT
	const unusable = impactPct >= UNUSABLE_IMPACT_PCT
	const impactUsd =
		impactQuote && impactOutId ? impactCostUsd(impactQuote, (raw) => usdOf(impactOutId, raw)) : null

	// Auto slippage: min_out must survive a couple of OTHER trades landing in
	// the same block, not just our own impact (which is already in the quote).
	// Impact-scaled with a floor, capped — past the cap the trade shouldn't
	// clear quietly anyway; the loud-impact confirm owns that conversation.
	const slippage = Math.min(15, Math.max(1, impactPct * 0.5 + 1))

	const minOut =
		plan &&
		((plan.swapQuote &&
			(plan.mode === 'swap' || plan.mode === 'swapToFrbtc' || plan.mode === 'wrapSwap')) ||
			plan.mode === 'curve')
			? applySlippage(plan.amountOut, slippage)
			: plan?.amountOut ?? 0n
	const feeRate = Math.max(
		1,
		Math.ceil(
			(selected === 'slow' ? speed?.slow : selected === 'fast' ? speed?.fast : speed?.medium) || 1
		)
	)

	// Fixed-point at PRICE_SCALE, not token decimals: at 8dp a real quote like
	// 1 CheekyB = 0.00002157 DIESEL truncated to a flat "0" — the exact
	// false-zero the price formatter exists to kill, in the highest-stakes row
	// on the page.
	// Compact past a million: "62,976,520,188.01" earns its digits nowhere on
	// a 0.75rem row — 62.98B carries the same information and survives the
	// no-wrap rule. Small rates keep the fixed-point precision (false-zero
	// protection matters exactly there).
	const rateNum = plan && plan.amountIn > 0n ? Number(plan.amountOut) / Number(plan.amountIn) : null
	const rate =
		plan && plan.amountIn > 0n
			? rateNum !== null && rateNum >= 1e6
				? numCompact(rateNum)
				: formatPrice((plan.amountOut * PRICE_SCALE) / plan.amountIn)
			: null

	// Wrap and wrap-then-swap spend plain BTC — no alkane carriers needed.
	const needsCarriers = mode !== 'wrap' && mode !== 'wrapSwap'
	const carrierAmount = needsCarriers && plan ? plan.amountIn : null
	const carriers = React.useMemo(() => {
		if (!carrierAmount || !Array.isArray(sellOutpoints)) {
			return null
		}
		const usable = sellOutpoints.filter((o: any) => !o.escrow)
		const picked: [string, number][] = []
		let covered = 0n
		for (const o of usable) {
			const raw = parseAmount(String(o.amount))
			if (!raw || raw <= 0n) {
				continue
			}
			picked.push([o.outpoint, o.sats])
			covered += raw
			if (covered >= carrierAmount) {
				break
			}
		}
		return covered >= carrierAmount ? picked : null
	}, [sellOutpoints, carrierAmount])

	/// The frBTC carriers a top-up plan sends: enough usable outpoints to
	/// cover `carryFrbtc`. The plan capped `carryFrbtc` at the same set's
	/// total, so null only means the outpoints are still loading (or moved
	/// under us) — the submit gate waits rather than sending a short set.
	const topupCarriers = React.useMemo(() => {
		if (!plan?.topup || !Array.isArray(sellOutpoints)) {
			return null
		}
		const picked: [string, number][] = []
		let covered = 0n
		for (const o of sellOutpoints) {
			if (o.escrow) {
				continue
			}
			const raw = parseAmount(String(o.amount))
			if (!raw || raw <= 0n) {
				continue
			}
			picked.push([o.outpoint, o.sats])
			covered += raw
			if (covered >= plan.topup.carryFrbtc) {
				break
			}
		}
		return covered >= plan.topup.carryFrbtc ? picked : null
	}, [sellOutpoints, plan])

	const verb =
		mode === 'wrap'
			? 'Wrap'
			: mode === 'wrapSwap'
			? 'Swap'
			: mode === 'unwrap'
			? 'Unwrap'
			: mode === 'swapToFrbtc'
			? 'Swap to frBTC'
			: 'Swap'
	/// Short form for the confirm button, which already sits under a heading.
	const shortVerb = mode === 'wrap' ? 'Wrap' : mode === 'unwrap' ? 'Unwrap' : 'Swap'

	const closeReview = React.useCallback(() => {
		// Bump the sequence so an in-flight build can't land on a closed review.
		prepSeq.current += 1
		setReview(null)
		setPrep({ status: 'idle' })
		onReviewChange?.(false)
	}, [onReviewChange])

	// The host's entry is the source of truth for the amount, adopted during
	// render (the derived-state pattern — no effect, no extra paint). Any
	// change while the review is open invalidates it and drops back to the
	// live invoice — the same rule the old editable fields enforced.
	const sellProp = plain(sellAmount ?? '')
	const buyProp = plain(buyAmount ?? '')
	const [adopted, setAdopted] = React.useState({ sell: sellProp, buy: buyProp })
	if (adopted.sell !== sellProp || adopted.buy !== buyProp) {
		setAdopted({ sell: sellProp, buy: buyProp })
		setSellInput(sellProp)
		setBuyInput(buyProp)
		setLastEdited(buyProp && !sellProp ? 'buy' : 'sell')
		// Inline closeReview, minus the sequence bump (refs are off-limits in
		// render). Safe without it: an in-flight build can then only land on a
		// CLOSED review (prep is never read while review is null), and the
		// next openReview bumps the sequence before building, which strands
		// any straggler for good.
		setReview(null)
		setPrep({ status: 'idle' })
	}

	// Opening the review unmounts the button that was just clicked, so focus
	// falls to <body> and — on a 360px rail inside a 900px viewport — the
	// Cancel/Confirm row can land below the fold with nothing visibly changed.
	// Pull the viewport and the keyboard onto the block itself.
	const reviewRef = React.useRef<HTMLDivElement>(null)
	const reviewOpen = review !== null
	React.useEffect(() => {
		const el = reviewRef.current
		if (!reviewOpen || !el) {
			return
		}
		const reduced =
			typeof window.matchMedia === 'function' &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches
		el.scrollIntoView({ block: 'nearest', behavior: reduced ? 'auto' : 'smooth' })
		// preventScroll: the smooth scroll above owns the movement; letting
		// focus() jump as well would cancel it mid-animation.
		el.focus({ preventScroll: true })
	}, [reviewOpen])

	const makeSnapshot = (): Review | null => {
		if (!sell || !buy || !plan || !outToken) {
			return null
		}
		if (plan.topup && topupCarriers === null) {
			// The submit gate should have held the button; never freeze a
			// top-up snapshot without the carriers it promises to spend.
			return null
		}
		return {
			plan,
			sell,
			buy,
			outToken,
			verb: shortVerb,
			minOut,
			maxIn: maxInput(plan.amountIn, slippage),
			slippage,
			feeRate,
			carriers,
			topup: plan.topup && topupCarriers ? { ...plan.topup, outpoints: topupCarriers } : null,
			impactPct,
			impactUsd,
			loud: highImpact
		}
	}

	/// Direct trade (2026-09-02): the CTA signs straight away — the wallet's
	/// own prompt is the confirmation, and the invoice above the button IS
	/// the review. The frozen-review stage below stays wired but dormant;
	/// high-impact trades keep their loud CTA (red, impact in the label).
	const signNow = () => {
		const snapshot = makeSnapshot()
		if (!snapshot) {
			return
		}
		const fresh =
			prep.status === 'ready' && Date.now() - prep.at < PREP_TTL_MS
				? { psbt: prep.psbt, chain: prep.chain }
				: undefined
		void submit(snapshot, fresh)
	}

	const openReview = () => {
		const built = makeSnapshot()
		if (!built) {
			return
		}
		const snapshot: Review = built
		setReview(snapshot)
		onReviewChange?.(true)
		const seq = (prepSeq.current += 1)
		setPrep({ status: 'building' })
		// Build the real transaction now so the fee on the review block is the
		// fee that gets signed, not a number the client made up.
		buildPsbt(snapshot).then(
			({ psbt, fee, chain }) => {
				if (prepSeq.current === seq) {
					setPrep({ status: 'ready', psbt, fee, at: Date.now(), chain })
				}
			},
			(e) => {
				if (prepSeq.current !== seq) {
					return
				}
				if (e instanceof Error && e.message === TOPUP_UNSUPPORTED) {
					// The server failed the capability echo — this session now
					// plans full wraps only, and this stale top-up review is
					// withdrawn before anything could be signed against it.
					markWrapSwapTopupUnsupported()
					closeReview()
					toast({
						title: 'Top-up unavailable',
						description:
							'This server cannot spend held frBTC alongside a wrap yet — review again to wrap the full amount.'
					})
					return
				}
				setPrep({ status: 'failed' })
			}
		)
	}

	const submit = React.useCallback(
		async (r: Review, prebuilt?: { psbt: string; chain?: ChainBuilt }) => {
			if (submitting) {
				return
			}
			setSubmitting(true)
			// The account that signs owns the records below — pinned before any
			// await, so a mid-flight account switch cannot re-attribute them.
			const owner = Wallet.getTaprootAddress()
			try {
				const built = prebuilt ?? (await buildPsbt(r))

				if (r.plan.mode === 'wrapSwap') {
					// One action, two transactions. Sign BOTH before broadcasting
					// ANYTHING: the swap references the wrap's pre-sign txid, so a
					// txid that moved during signing must abort with zero txs sent
					// — after a broadcast it would strand the minted frBTC.
					const chain = built.chain
					if (!chain) {
						throw new Error('chained build unavailable — try again')
					}
					const signedWrap = await Wallet.signPSBTExtractTx(chain.wrapPsbt)
					const signedSwap = await Wallet.signPSBTExtractTx(chain.swapPsbt)
					if (
						Wallet.txidOfRawTx(signedWrap) !== chain.wrapTxid ||
						Wallet.txidOfRawTx(signedSwap) !== chain.swapTxid
					) {
						throw new Error(
							'signing changed a transaction id — nothing was broadcast. Please retry.'
						)
					}
					const wrapRes = await axios.post(`${API_URL}/wallet/broadcast`, {
						rawtx: signedWrap
					})
					if (wrapRes.data.error) {
						throw new Error(wrapRes.data.message || 'The network rejected the wrap.')
					}
					// A top-up wraps only the shortfall — the wrap record must
					// carry the sats that actually hit the signer, not the total.
					const wrapped = r.topup ? r.topup.wrapSats : r.plan.amountIn
					const minted = wrapOut(wrapped)
					recordSwap(owner, {
						ts: Date.now(),
						txid: chain.wrapTxid,
						sellId: BTC_ID,
						sellName: 'BTC',
						buyId: FRBTC_ID,
						buyName: 'frBTC',
						amountInRaw: wrapped.toString(),
						quotedOutRaw: minted.toString(),
						minOutRaw: minted.toString(),
						kind: 'wrap',
						intentBuyId: r.buy.id,
						intentBuyName: r.buy.name || r.buy.id
					})
					const swapRes = await axios.post(`${API_URL}/wallet/broadcast`, {
						rawtx: signedSwap
					})
					if (swapRes.data.error) {
						// The wrap IS out. Fall back to the old guided flow so the
						// minted frBTC is completable, not stranded.
						setPendingWrap({
							txid: chain.wrapTxid,
							minted,
							intent: r.buy,
							atHeight: tipHeight,
							frbtcBefore: balances.get(FRBTC_ID) ?? 0n
						})
						throw new Error(
							`${
								swapRes.data.message || 'The network rejected the swap.'
							} The wrap already broadcast — finish the swap from the banner once it confirms.`
						)
					}
					recordSwap(owner, {
						ts: Date.now(),
						txid: chain.swapTxid,
						sellId: FRBTC_ID,
						sellName: 'frBTC',
						buyId: r.outToken.id,
						buyName: r.outToken.name || r.outToken.id,
						// mint + carried on a top-up; equals the mint otherwise.
						amountInRaw: chain.swapAmountIn.toString(),
						quotedOutRaw: (r.plan.swapQuote?.amountOut ?? r.plan.amountOut).toString(),
						minOutRaw: r.minOut.toString(),
						kind: 'swap'
					})
					setSellInput('')
					setBuyInput('')
					onSwapped()
					mutate(`${API_URL}/alkane/pools`)
					if (onBroadcast) {
						onBroadcast({
							kind: 'swap',
							txid: chain.swapTxid,
							sellId: BTC_ID,
							sellName: 'BTC',
							outId: r.outToken.id,
							outName: r.outToken.name || r.outToken.id,
							amountInRaw: r.plan.amountIn.toString(),
							amountOutRaw: (r.plan.swapQuote?.amountOut ?? r.plan.amountOut).toString()
						})
					} else {
						toast({
							title: 'Swap submitted',
							description: `${formatAmount(r.plan.amountIn)} BTC → ${
								r.outToken.name || r.outToken.id
							} — two transactions, confirming together`,
							action: (
								<ToastAction
									altText="View swap transaction"
									onClick={() => window.open(`${ESPO_URL}/tx/${chain.swapTxid}`)}
								>
									View
								</ToastAction>
							)
						})
					}
					return
				}

				const psbt = built.psbt
				const rawtx = await Wallet.signPSBTExtractTx(psbt)
				const { data } = await axios.post(`${API_URL}/wallet/broadcast`, { rawtx })
				if (data.error) {
					toast({
						variant: 'destructive',
						title: 'Broadcast failed',
						description: data.message || 'The network rejected the transaction.'
					})
					return
				}
				const txid = data.result
				if (r.plan.mode === 'wrap') {
					// Exactly what the signer mints for this input — the exact-out
					// path asks for slightly less than the wrap actually returns.
					const minted = wrapOut(r.plan.amountIn)
					recordSwap(owner, {
						ts: Date.now(),
						txid,
						sellId: BTC_ID,
						sellName: 'BTC',
						buyId: FRBTC_ID,
						buyName: 'frBTC',
						amountInRaw: r.plan.amountIn.toString(),
						quotedOutRaw: minted.toString(),
						minOutRaw: minted.toString(),
						kind: 'wrap'
					})
					setSellInput('')
					setBuyInput('')
					onSwapped()
					// The panel stays mounted: the wrap reports its own progress in
					// a banner.
					setPendingWrap({
						txid,
						minted,
						intent: null,
						atHeight: tipHeight,
						frbtcBefore: balances.get(FRBTC_ID) ?? 0n
					})
					onBroadcast?.({
						kind: 'wrap',
						txid,
						sellId: BTC_ID,
						sellName: 'BTC',
						outId: FRBTC_ID,
						outName: 'frBTC',
						amountInRaw: r.plan.amountIn.toString(),
						amountOutRaw: minted.toString()
					})
				} else if (r.plan.mode === 'unwrap') {
					recordSwap(owner, {
						ts: Date.now(),
						txid,
						sellId: FRBTC_ID,
						sellName: 'frBTC',
						buyId: BTC_ID,
						buyName: 'BTC',
						amountInRaw: r.plan.amountIn.toString(),
						quotedOutRaw: r.plan.amountOut.toString(),
						minOutRaw: r.plan.amountOut.toString(),
						kind: 'unwrap'
					})
					setSellInput('')
					setBuyInput('')
					onSwapped()
					if (onBroadcast) {
						onBroadcast({
							kind: 'unwrap',
							txid,
							sellId: FRBTC_ID,
							sellName: 'frBTC',
							outId: BTC_ID,
							outName: 'BTC',
							amountInRaw: r.plan.amountIn.toString(),
							amountOutRaw: r.plan.amountOut.toString()
						})
					} else {
						toast({
							title: 'Unwrap submitted',
							description:
								'The signer settles unwraps asynchronously — BTC arrives after the federation processes it.',
							action: (
								<ToastAction
									altText="View transaction"
									onClick={() => window.open(`${ESPO_URL}/tx/${txid}`)}
								>
									View
								</ToastAction>
							)
						})
					}
				} else {
					recordSwap(owner, {
						ts: Date.now(),
						txid,
						sellId: r.sell.id,
						sellName: r.sell.name || r.sell.id,
						buyId: r.outToken.id,
						buyName: r.outToken.name || r.outToken.id,
						amountInRaw: r.plan.amountIn.toString(),
						quotedOutRaw: (r.plan.swapQuote?.amountOut ?? r.plan.amountOut).toString(),
						minOutRaw: r.minOut.toString(),
						kind: 'swap'
					})
					setSellInput('')
					setBuyInput('')
					onSwapped()
					mutate(`${API_URL}/alkane/pools`)
					if (onBroadcast) {
						onBroadcast({
							kind: 'swap',
							txid,
							sellId: r.sell.id,
							sellName: r.sell.name || r.sell.id,
							outId: r.outToken.id,
							outName: r.outToken.name || r.outToken.id,
							amountInRaw: r.plan.amountIn.toString(),
							amountOutRaw: (r.plan.swapQuote?.amountOut ?? r.plan.amountOut).toString()
						})
					} else {
						toast({
							title: 'Swap submitted',
							description: `${formatAmount(r.plan.amountIn)} ${r.sell.name || r.sell.id} → ${
								r.outToken.name || r.outToken.id
							}`,
							action: (
								<ToastAction
									altText="View transaction"
									onClick={() => window.open(`${ESPO_URL}/tx/${txid}`)}
								>
									View
								</ToastAction>
							)
						})
					}
				}
			} catch (e: any) {
				if (e instanceof Error && e.message === TOPUP_UNSUPPORTED) {
					// A stale prep re-built at submit time and failed the
					// capability echo — nothing was signed. Downgrade and ask
					// for a fresh (full-wrap) review.
					markWrapSwapTopupUnsupported()
					toast({
						variant: 'destructive',
						title: 'Top-up unavailable',
						description:
							'This server cannot spend held frBTC alongside a wrap yet — review the trade again to wrap the full amount.'
					})
					return
				}
				toast({
					variant: 'destructive',
					title:
						r.plan.mode === 'wrap' || r.plan.mode === 'wrapSwap'
							? 'Wrap failed'
							: r.plan.mode === 'unwrap'
							? 'Unwrap failed'
							: 'Swap failed',
					description: e?.response?.data?.message || e?.message || 'Something went wrong.'
				})
			} finally {
				setSubmitting(false)
				closeReview()
			}
		},
		[
			submitting,
			toast,
			mutate,
			onSwapped,
			onBroadcast,
			closeReview,
			tipHeight,
			balances,
			markWrapSwapTopupUnsupported
		]
	)

	// The quote keeps moving under an open review. If it moves past what the
	// user approved, the confirm button becomes a re-review instead of signing
	// a trade they never saw.
	const moved =
		review !== null &&
		(plan === null ||
			plan.mode !== review.plan.mode ||
			quoteMoved(plan, { minOut: review.minOut, maxIn: review.maxIn }))

	// Two-step (phone sheet): the entry CTA promises the REVIEW, not the
	// trade — "Buy" on a button that opens a second screen is a lie the user
	// discovers by tapping it.
	const stagedPrimary = staged
		? `Review ${(reviewLabel ?? verb).toLowerCase()}`
		: reviewLabel ?? verb
	const buttonLabel = marketDataDown
		? // Ahead of every other state: with no reserves there is nothing to
		  // quote, so "Connect wallet" would be an invitation into a dead end.
		  'Market data unavailable'
		: balancesUnavailable
		? 'Balances unavailable'
		: carrierDataUnavailable
		? 'Trade inputs unavailable'
		: watchMode
		? 'Watch mode — read only'
		: !loggedIn
		? 'Connect wallet'
		: !sell || !buy
		? 'Select tokens'
		: !hasAmount
		? 'Enter an amount'
		: insufficient
		? `Insufficient ${sell.name || sell.id}`
		: noRoute
		? 'No route'
		: unusable
		? 'Amount too large for this pool'
		: (needsCarriers && carriers === null) || (plan?.topup && topupCarriers === null)
		? 'Loading balances…'
		: submitting
		? 'Confirm in wallet…'
		: !staged && plan !== null && (prep.status === 'building' || prep.status === 'idle')
		? // Two-step hosts review the frozen figures while the PSBT builds;
		  // the review's own confirm button carries "Preparing…".
		  'Preparing…'
		: highImpact
		? `${stagedPrimary} — ${impactPct.toFixed(1)}% impact`
		: stagedPrimary

	// A pool that would move 50%+ isn't quoting a price, and letting the button
	// stay live implies otherwise.
	// The invoice's network-fee figure is the REAL transaction's fee (the
	// v2-feed invoice pattern, same debounce discipline as
	// search-input/components/invoice.tsx): the build INPUTS are debounced —
	// not the call site — a started-key ref skips StrictMode's double-run
	// before any HTTP goes out, and the sequence counter discards stale
	// results. `planReady` is every submit gate EXCEPT the prepared-PSBT one:
	// keying the build on `canSubmit` would deadlock, since canSubmit now
	// WAITS for the build.
	const planReady =
		!marketDataDown &&
		!balancesUnavailable &&
		!carrierDataUnavailable &&
		!watchMode &&
		loggedIn &&
		plan !== null &&
		!insufficient &&
		!noRoute &&
		(!needsCarriers || carriers !== null) &&
		(!plan?.topup || topupCarriers !== null)
	const prepKey =
		planReady && plan !== null && sell && buy
			? [
					plan.mode,
					plan.amountIn,
					minOut,
					feeRate,
					sell.id,
					buy.id,
					plan.topup ? plan.topup.wrapSats : 0n
			  ].join(':')
			: null
	const debouncedPrepKey = useDebounce(prepKey, 500)
	const startedPrepKey = React.useRef<string>('')
	React.useEffect(() => {
		if (prepKey === null) {
			prepSeq.current += 1
			startedPrepKey.current = ''
			setPrep({ status: 'idle' })
			return
		}
		if (prepKey !== startedPrepKey.current && prepKey !== debouncedPrepKey) {
			// The key moved and the debounce hasn't settled: the row shows
			// building, and no request goes out until typing stops.
			prepSeq.current += 1
			setPrep({ status: 'building' })
			return
		}
		if (debouncedPrepKey !== prepKey || startedPrepKey.current === debouncedPrepKey) {
			return
		}
		startedPrepKey.current = debouncedPrepKey
		const seq = (prepSeq.current += 1)
		const snapshot = makeSnapshot()
		if (!snapshot) {
			setPrep({ status: 'idle' })
			return
		}
		setPrep({ status: 'building' })
		buildPsbt(snapshot).then(
			({ psbt, fee, chain }) => {
				if (prepSeq.current === seq) {
					setPrep({ status: 'ready', psbt, fee, at: Date.now(), chain })
				}
			},
			() => {
				if (prepSeq.current === seq) {
					setPrep({ status: 'failed' })
				}
			}
		)
		// makeSnapshot/buildPsbt re-create per render; the KEY names every
		// input that changes the transaction's bytes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [prepKey, debouncedPrepKey])

	// The click signs the PREPARED transaction — so the button waits for it.
	// 'failed' stays clickable: submit rebuilds and surfaces the real error
	// instead of a silently dead button. planReady is defined above the
	// prebuild effect (it is also its key — and deliberately excludes this
	// prep gate, which would deadlock the build).
	const canSubmit =
		planReady && (prep.status === 'ready' || prep.status === 'failed') && !submitting
	// Two-step hosts: step one freezes the figures and needs only a plan —
	// the PSBT keeps building underneath and gates the review's own button.
	const ctaReady = staged && review === null ? planReady && !submitting : canSubmit


	// frBTC arrival is read from the wallet, not assumed from elapsed blocks.
	const wrapArrived =
		pendingWrap !== null && (balances.get(FRBTC_ID) ?? 0n) > pendingWrap.frbtcBefore
	React.useEffect(() => {
		if (!pendingWrap || wrapArrived) {
			return
		}
		const t = setInterval(() => onSwapped(), WRAP_POLL_MS)
		return () => clearInterval(t)
	}, [pendingWrap, wrapArrived, onSwapped])

	const continueToSwap = () => {
		if (!pendingWrap?.intent) {
			return
		}
		onSelectSell(frbtcToken)
		onSelectBuy(pendingWrap.intent)
		setSellInput(plain(formatAmount(pendingWrap.minted)))
		setLastEdited('sell')
		setPendingWrap(null)
	}

	const feeUsd = prep.status === 'ready' ? usdOf(BTC_ID, prep.fee) : null
	const sendUsd = review ? usdOf(review.sell.id, review.plan.amountIn) : null
	const receiveUsd = review ? usdOf(review.outToken.id, review.plan.amountOut) : null

	// Instant-trade intents: the host bumps `reviewRequest` after setting the
	// amount, and the frozen review opens as soon as the plan and its carriers
	// can honestly freeze. An amount change after the bump cancels it — the
	// user took over. Runs unconditionally per render because "ready" is a
	// composite of quote, balances and carrier state.
	const armedReview = React.useRef<{ sell: string; buy: string } | null>(null)
	const seenReviewRequest = React.useRef(reviewRequest ?? 0)
	React.useEffect(() => {
		if (reviewRequest !== undefined && reviewRequest !== seenReviewRequest.current) {
			seenReviewRequest.current = reviewRequest
			armedReview.current = { sell: sellProp, buy: buyProp }
		} else if (
			armedReview.current &&
			(armedReview.current.sell !== sellProp || armedReview.current.buy !== buyProp)
		) {
			armedReview.current = null
		}
		if (armedReview.current && review === null && canSubmit) {
			armedReview.current = null
			signNow()
		}
	})

	// Announced to screen readers: the submit label as it walks through its
	// states, and the whole review summary the moment the block opens.
	const announcement = review
		? moved
			? 'The quote moved past the minimum you reviewed. Review again before signing.'
			: `Review ${review.verb.toLowerCase()}: send ${formatAmount(review.plan.amountIn)} ${
					review.sell.name || review.sell.id
			  }, receive ${formatAmount(review.plan.amountOut)} ${
					review.outToken.name || review.outToken.id
			  }, minimum ${formatAmount(review.minOut)}. Confirm or cancel.`
		: // Outside the review the useful announcement is the QUOTE, not the
		// button label: the output amount and the impact tier are what change
		// as the user types, and they were previously silent (the summary <dl>
		// is deliberately not a live region — it re-announced every row on
		// every 20s refresh).
		plan && outToken && hasAmount
		? `${formatAmount(plan.amountOut)} ${outToken.name || outToken.id}${
				impactPct >= 1.5 ? `, price impact ${impactPct.toFixed(1)} percent` : ''
		  }${unusable ? ' — amount too large for this pool' : ''}`
		: buttonLabel

	// ——— Presentation from here down. Nothing below owns money logic: every
	// figure renders from the plan, the frozen review, or the real prep. ———

	/// Fees, itemized in one row: only the legs this action actually has.
	const feeParts: string[] = []
	if (mode === 'wrap' || mode === 'wrapSwap') {
		feeParts.push('0.1% wrap')
	}
	if (mode === 'unwrap') {
		feeParts.push('0.1% unwrap')
	}
	/// Venue tags only when SUBFROSTAMM is involved — an all-oyl route reads as
	/// it always has.
	const showVenues = !!plan?.swapQuote?.hops.some((h) => h.venue === 'subfrostamm')
	const hopFeeLabel = (h: { venue: string; feeNum: bigint; feeDen: bigint }) =>
		`${pctLabel(hopFeePct(h))}${showVenues ? ` ${venueName(h.venue)}` : ''}`
	if (plan?.swapQuote) {
		feeParts.push(`${plan.swapQuote.hops.map(hopFeeLabel).join('+')} pool`)
		if (ROUTER_ENABLED) {
			feeParts.push(`${routerFeePct(routerFeePer1000)} router`)
		}
	}
	if (plan?.mode === 'curve') {
		// CryptoSwap's fee is dynamic (0.2–0.8% by imbalance) and ALREADY
		// inside the quoted rate — an itemized number here would double-count.
		feeParts.push('pool fee in rate')
	}
	/// The itemization as invoice rows — label left, figure right, one line
	/// each — for the hover panel.
	const feeRows: { label: string; value: string }[] = []
	if (mode === 'wrap' || mode === 'wrapSwap') {
		feeRows.push({ label: 'Wrap', value: '0.1%' })
	}
	if (mode === 'unwrap') {
		feeRows.push({ label: 'Unwrap', value: '0.1%' })
	}
	if (plan?.swapQuote) {
		feeRows.push({
			label: 'Pool',
			value: plan.swapQuote.hops.map(hopFeeLabel).join(' + ')
		})
		if (ROUTER_ENABLED) {
			feeRows.push({ label: 'Router', value: routerFeePct(routerFeePer1000) })
		}
	}
	if (plan?.mode === 'curve') {
		feeRows.push({ label: 'Pool', value: 'in rate (0.2–0.8%)' })
	}
	// The one figure the route line shows; the itemization above lives in its
	// hover panel. Additive across legs — the compounding error at these
	// sizes is under a basis point, and the review's numbers are exact anyway.
	const feeTotalPct = (() => {
		let t = 0
		if (mode === 'wrap' || mode === 'wrapSwap') {
			t += 0.1
		}
		if (mode === 'unwrap') {
			t += 0.1
		}
		if (plan?.swapQuote) {
			t += plan.swapQuote.hops.reduce((a, h) => a + hopFeePct(h), 0)
			if (ROUTER_ENABLED) {
				t += Number(routerFeePer1000) / 10
			}
		}
		return t
	})()

	/// The route, before and after the quote lands: the quoted path when there
	/// is one, the pair's shape while it streams in.
	const routePath: string[] | null = plan
		? plan.displayPath
		: sell && outToken
		? mode === 'wrapSwap'
			? [BTC_ID, FRBTC_ID, outToken.id]
			: [sell.id, outToken.id]
		: null

	const tokenName = (id: string) => tokens.find((x) => x.id === id)?.name || id

	return (
		<section aria-label="Trade" data-testid="alkanes-swap-panel" className="flex flex-col">
			{pendingWrap && (
				<div
					data-testid="alkanes-wrap-pending"
					role="status"
					aria-live="polite"
					className={cn(
						'mb-3 flex flex-col gap-1.5 rounded-xl border px-3 py-2.5',
						wrapArrived ? 'border-success/40 bg-success/10' : 'border-border bg-secondary/50'
					)}
				>
					<div className="flex items-baseline justify-between gap-2">
						<h3 className="text-xs font-semibold">
							{wrapArrived
								? 'frBTC arrived'
								: pendingWrap.intent
								? 'Step 1 of 2 — wrap pending'
								: 'Wrap pending'}
						</h3>
						<button
							type="button"
							onClick={() => setPendingWrap(null)}
							className="alkanes-touch rounded px-1 text-xxs text-muted-foreground transition-colors hover:text-foreground"
						>
							Dismiss
						</button>
					</div>
					<p className="text-xxs leading-relaxed text-muted-foreground">
						{wrapArrived
							? pendingWrap.intent
								? `Your frBTC is spendable — continue to ${
										pendingWrap.intent.name || pendingWrap.intent.id
								  } at the current quote.`
								: 'Your frBTC is spendable.'
							: `${formatAmount(pendingWrap.minted, 5)} frBTC lands after one confirmation${
									pendingWrap.atHeight !== null && tipHeight !== null
										? ` — broadcast at block ${pendingWrap.atHeight.toLocaleString(
												'en-US'
										  )}, tip is ${tipHeight.toLocaleString('en-US')}`
										: ''
							  }.${
									pendingWrap.intent
										? ` The swap to ${
												pendingWrap.intent.name || pendingWrap.intent.id
										  } is a second transaction, re-quoted then.`
										: ''
							  } This checks your wallet every 30 seconds and also updates on your next load.`}
					</p>
					<a
						href={`${ESPO_URL}/tx/${pendingWrap.txid}`}
						target="_blank"
						rel="noreferrer"
						className="inline-flex items-center gap-1 text-xxs tabular-nums text-primary hover:underline"
					>
						{pendingWrap.txid.slice(0, 16)}… <ExternalLink className="size-3" />
					</a>
					{wrapArrived && pendingWrap.intent && (
						<Button
							data-testid="alkanes-wrap-continue"
							variant="secondary"
							className="mt-1 w-full"
							onClick={continueToSwap}
						>
							Swap frBTC → {pendingWrap.intent.name || pendingWrap.intent.id}
						</Button>
					)}
				</div>
			)}

			<p className="sr-only" role="status" aria-live="polite">
				{announcement}
			</p>

			{review ? (
				/* ——— The confirm stage: the SAME surface, next stage. Everything
				   here reads from the frozen Review snapshot — the labeled lock is
				   the visible promise that these figures cannot move under the
				   signature. The quote-moved guard turns the confirm button into a
				   re-review instead of signing a trade the user never saw. ——— */
				<div
					ref={reviewRef}
					tabIndex={-1}
					data-testid="alkanes-confirm"
					onKeyDown={(e) => {
						// Escape dismisses the review — a surface you can open but
						// not close by keyboard is a trap at the moment money is
						// about to move.
						if (e.key === 'Escape') {
							e.stopPropagation()
							closeReview()
						}
					}}
					role="group"
					aria-label="Review before signing — figures locked"
					className={cn(
						'alkanes-stage-in scroll-mt-16 scroll-mb-12 space-y-2.5 rounded-2xl border p-3.5 outline-none',
						review.loud ? 'border-destructive bg-destructive/10' : 'border-border bg-surface-2'
					)}
				>
					<div className="flex items-baseline justify-between gap-2">
						<h3 className={cn('text-sm font-semibold', review.loud && 'text-destructive-ink')}>
							{review.loud
								? `High price impact — ${review.impactPct.toFixed(1)}%`
								: reviewLabel ?? `Review ${review.verb.toLowerCase()}`}
						</h3>
						<span
							data-testid="alkanes-confirm-lock"
							className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-xxs font-semibold text-muted-foreground"
						>
							Figures locked
						</span>
					</div>

					<dl className="space-y-1.5 text-xs">
						<div className="flex items-baseline justify-between gap-3">
							<dt className="text-muted-foreground">You send</dt>
							<dd className="text-right">
								<span className="block font-semibold tabular-nums">
									{formatAmount(review.plan.amountIn)} {review.sell.name || review.sell.id}
								</span>
								{sendUsd !== null && (
									<span className="block text-xxs tabular-nums text-muted-foreground">
										{formatUsd(sendUsd)}
									</span>
								)}
							</dd>
						</div>
						<div className="flex items-baseline justify-between gap-3">
							<dt className="text-muted-foreground">You receive</dt>
							<dd className="text-right">
								<span className="block font-semibold tabular-nums">
									{formatAmount(review.plan.amountOut)} {review.outToken.name || review.outToken.id}
								</span>
								{review.outToken.id === FRBTC_ID && review.plan.mode !== 'wrap' ? (
									<span className="block text-xxs tabular-nums text-muted-foreground">
										= {formatAmount(review.plan.amountOut)} BTC
										{receiveUsd !== null ? ` · ${formatUsd(receiveUsd)}` : ''} — 1:1 with BTC
									</span>
								) : (
									receiveUsd !== null && (
										<span className="block text-xxs tabular-nums text-muted-foreground">
											{formatUsd(receiveUsd)}
										</span>
									)
								)}
							</dd>
						</div>
						<div className="flex items-baseline justify-between gap-3">
							<dt className="text-muted-foreground">
								{review.plan.swapQuote
									? `Minimum received (auto ${review.slippage.toFixed(1)}% tolerance)`
									: 'Minimum received'}
							</dt>
							<dd className="text-right tabular-nums">
								{formatAmount(review.minOut)} {review.outToken.name || review.outToken.id}
								{!review.plan.swapQuote && (
									<span className="block text-xxs text-muted-foreground">
										Fixed — 0.1% signer fee, no slippage
									</span>
								)}
							</dd>
						</div>
						<div className="flex items-baseline justify-between gap-3">
							<dt className="text-muted-foreground">Price impact</dt>
							<dd
								className={cn(
									'text-right tabular-nums',
									review.loud ? 'font-semibold text-destructive-ink' : impactTone(review.impactPct)
								)}
							>
								{review.plan.swapQuote ? (
									<React.Fragment>
										<span className={cn('block', review.loud && 'text-sm')}>
											{review.impactPct < 0.01 ? '<0.01' : review.impactPct.toFixed(2)}%
										</span>
										<span className="block text-xxs">
											{review.impactUsd !== null
												? `${formatUsd(review.impactUsd)} vs mid price`
												: 'dollar cost unavailable'}
										</span>
									</React.Fragment>
								) : (
									<span className="text-muted-foreground">None — fixed 0.1% fee</span>
								)}
							</dd>
						</div>
						{ROUTER_ENABLED && review.plan.swapQuote && (
							<div className="flex items-baseline justify-between gap-3">
								<dt className="text-muted-foreground">Router fee</dt>
								<dd className="text-right tabular-nums">
									<span className="block">{routerFeePct(routerFeePer1000)}</span>
									{/* The quote above is already net of this. Saying so keeps the
									    row from reading as a charge on top of "You receive". */}
									<span className="block text-xxs text-muted-foreground">
										Already deducted above
									</span>
								</dd>
							</div>
						)}
						<div className="flex items-baseline justify-between gap-3">
							<dt className="text-muted-foreground">Network fee</dt>
							<dd className="text-right tabular-nums">
								{prep.status === 'ready' ? (
									<React.Fragment>
										<span className="block">
											{feeUsd !== null ? formatUsd(feeUsd) : `${prep.fee.toString()} sats`}
										</span>
										<span className="block text-xxs text-muted-foreground">
											{prep.fee.toLocaleString('en-US')} sats at {review.feeRate} sat/vB
										</span>
									</React.Fragment>
								) : prep.status === 'building' ? (
									<span className="text-muted-foreground">{review.feeRate} sat/vB · sizing…</span>
								) : (
									<React.Fragment>
										<span className="block">{review.feeRate} sat/vB</span>
										<span className="block text-xxs text-muted-foreground">
											Total unavailable — your wallet shows it before you sign
										</span>
									</React.Fragment>
								)}
							</dd>
						</div>
					</dl>

					{review.plan.mode === 'wrapSwap' && (
						<p className="text-xxs leading-relaxed text-muted-foreground">
							{review.topup
								? `You sign two transactions that broadcast back-to-back: the wrap mints frBTC from ${formatAmount(
										review.topup.wrapSats
								  )} BTC, and the swap to ${
										review.buy.name || review.buy.id
								  } sells it together with ${formatAmount(
										review.topup.carryFrbtc
								  )} frBTC you already hold — same block.`
								: `You sign two transactions that broadcast back-to-back: the wrap mints frBTC and the swap to ${
										review.buy.name || review.buy.id
								  } spends it in the same block.`}
						</p>
					)}
					{review.plan.mode === 'swapToFrbtc' && (
						<p className="text-xxs leading-relaxed text-muted-foreground">
							This delivers frBTC — 1 frBTC = 1 BTC, spendable on your next buy. Unwrapping to
							native BTC is a separate transaction you start yourself.
						</p>
					)}
					<p
						className={cn(
							'text-xxs leading-relaxed',
							review.loud ? 'text-destructive-ink' : 'text-muted-foreground'
						)}
					>
						On-chain and irreversible once broadcast.
					</p>

					<div className="flex gap-2 pt-0.5">
						<Button
							data-testid="alkanes-confirm-cancel"
							variant="secondary"
							className="h-12 flex-1 rounded-xl"
							disabled={submitting}
							onClick={closeReview}
						>
							Cancel
						</Button>
						<Button
							data-testid="alkanes-confirm-accept"
							className={cn(
								'h-12 flex-1 rounded-xl font-bold',
								'disabled:opacity-100 disabled:bg-secondary disabled:text-muted-foreground',
								!submitting &&
									prep.status !== 'building' &&
									(moved
										? // NOT text-warning-ink-foreground — that class resolves
										  // to no rule at all (measured 2.04:1 dark) on the single
										  // most safety-critical state in the panel.
										  'bg-warning text-warning-foreground hover:bg-warning/90'
										: review.loud
										? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
										: 'bg-primary text-primary-foreground hover:bg-primary/90')
							)}
							disabled={submitting || prep.status === 'building'}
							onClick={() => {
								if (moved) {
									// Re-snapshot against the live quote instead of signing
									// something the user never reviewed.
									openReview()
									return
								}
								const fresh =
									prep.status === 'ready' && Date.now() - prep.at < PREP_TTL_MS
										? { psbt: prep.psbt, chain: prep.chain }
										: undefined
								void submit(review, fresh)
							}}
						>
							{submitting && <Loader2 className="mr-2 size-4 animate-spin" />}
							{submitting
								? 'Confirm in wallet…'
								: moved
								? 'Quote moved — review again'
								: prep.status === 'building'
								? 'Preparing…'
								: review.loud
								? `${reviewLabel ?? review.verb} anyway — ${review.impactPct.toFixed(1)}% impact`
								: `Confirm ${(reviewLabel ?? review.verb).toLowerCase()}`}
						</Button>
					</div>
				</div>
			) : (
				<React.Fragment>
					{/* ——— The inline invoice. Space is reserved by the reveal from
					    the first keystroke and never changes height again: four
					    fixed row slots (rate carries its impact, the route carries
					    its venue and total fee — itemized in the tooltip), each
					    value real or "—", and no row may wrap — so the quote
					    streams into place and the CTA below does not move. ——— */}
					<div
						className={cn(
							'alkanes-reveal',
							!staged && hasAmount && 'alkanes-reveal-open'
						)}
						aria-hidden={staged || !hasAmount}
					>
						<div className="min-h-0 overflow-hidden">
							<dl
								data-testid="alkanes-invoice"
								className="space-y-1.5 pb-0 pt-2 text-[clamp(0.625rem,2.9vw,0.75rem)] leading-5"
							>
								<div
									data-testid="alkanes-invoice-route"
									className="flex min-w-0 items-center justify-between gap-2 whitespace-nowrap"
								>
									{routePath ? (
										<span className="flex min-w-0 flex-nowrap items-center gap-0.5">
											{routePath.map((id, i) => {
												const t = tokens.find((x) => x.id === id)
												// Long routes name their ends and shrink the middle
												// hops to bare icons — the hop's name rides its
												// tooltip. Everything still fits one row.
												const iconOnly = routePath.length >= 4 && i > 0 && i < routePath.length - 1
												return (
													<React.Fragment key={id}>
														{i > 0 && (
															<span aria-hidden="true" className="text-muted-foreground">
																→
															</span>
														)}
														<span
															className="flex items-center gap-0.5"
															title={iconOnly ? tokenName(id) : undefined}
														>
															{t && <TokenIcon token={t} size={12} />}
															{!iconOnly && tokenName(id)}
														</span>
													</React.Fragment>
												)
											})}
										</span>
									) : (
										<span className="text-muted-foreground">Route</span>
									)}
									<span className="flex shrink-0 items-center gap-1">
										{plan?.mode === 'curve' ? (
											<span className="rounded bg-primary/10 px-1 py-px text-xxs font-semibold text-primary">
												Curve
											</span>
										) : showVenues ? (
											<span
												data-testid="alkanes-invoice-venue"
												title="Routes through the Ordinals Wallet router on the SUBFROST AMM (factory 4:333052)"
												className="rounded bg-primary/10 px-1 py-px text-xxs font-semibold text-primary"
											>
												{plan?.swapQuote?.hops.every((h) => h.venue === 'subfrostamm')
													? 'SUBFROST AMM'
													: 'Oyl + SUBFROST'}
											</span>
										) : plan?.mode === 'wrap' || plan?.mode === 'unwrap' ? (
											<span className="rounded bg-secondary px-1 py-px text-xxs font-semibold text-muted-foreground">
												Signer
											</span>
										) : null}
										{plan && (feeTotalPct > 0 || plan.mode === 'curve') && (
											<HoverDetail
												heading="Fees"
												align="right"
												detail={feeRows.map((r) => (
													<span
														key={r.label + r.value}
														className="flex items-center justify-between gap-6 whitespace-nowrap"
													>
														<span className="text-muted-foreground">{r.label}</span>
														<span className="tabular-nums">{r.value}</span>
													</span>
												))}
											>
												<span
													data-testid="alkanes-invoice-fees"
													data-fees={feeParts.join(' · ')}
													className="text-muted-foreground"
												>
													{plan.mode === 'curve'
														? 'fee in rate'
														: `~${feeTotalPct.toFixed(feeTotalPct % 1 === 0 ? 0 : 1)}% fee`}
												</span>
											</HoverDetail>
										)}
									</span>
								</div>
								{/* Rate and impact are ONE claim about the same number —
								    what a unit costs here versus at spot — so they share a
								    row: the impact rides the rate as a toned suffix, and
								    the row count drops without dropping a fact. */}
								<InvoiceRow label="Rate" testId="alkanes-invoice-rate">
									{rate && sell && outToken ? (
										<span className="flex max-w-full flex-nowrap items-baseline justify-end gap-x-1">
											<span className="min-w-0 truncate">
												{`1 ${sell.name || sell.id} = ${rate} ${outToken.name || outToken.id}`}
											</span>
											{plan?.swapQuote || plan?.mode === 'curve' ? (
												<span className={cn('shrink-0', impactTone(impactPct))}>
													· {impactPct < 0.01 ? '<0.01' : impactPct.toFixed(2)}%
													{impactUsd !== null && impactUsd > 0 ? ` (${formatUsd(impactUsd)})` : ''}
													{unusable ? ' — too large' : ''}
												</span>
											) : plan ? (
												<span className="shrink-0 text-muted-foreground">· 0.1% fee</span>
											) : null}
										</span>
									) : (
										dash
									)}
								</InvoiceRow>
								{/* The top-up rung spends held frBTC alongside the wrap — a
								    fact the invoice must state, or the frBTC a user wrapped
								    earlier simply "disappears" into the next buy. (Fresh's
								    wallet, 2026-09-03: a 0.0001 BTC wrap rode a later
								    0.0005 BTC buy with no line saying so.) */}
								{plan?.topup && (
									<InvoiceRow label="Funding" testId="alkanes-invoice-funding">
										{`${formatAmount(plan.topup.wrapSats)} BTC wrapped + ${formatAmount(
											plan.topup.carryFrbtc
										)} frBTC you hold`}
									</InvoiceRow>
								)}
								<InvoiceRow
									label={plan && !plan.swapQuote ? 'You receive' : 'Min. received'}
									testId="alkanes-invoice-min"
								>
									{plan && outToken
										? `${
												minOut >= 100_000_000_000_000n
													? numCompact(Number(minOut) / 1e8)
													: formatAmount(minOut)
										  } ${outToken.name || outToken.id}`
										: dash}
								</InvoiceRow>
								<div
									data-testid="alkanes-invoice-network"
									className="flex min-w-0 items-center justify-between gap-2 whitespace-nowrap"
								>
									<span className="flex shrink-0 items-center gap-0.5">
										{/* The row's name for readers — the levels alone are three
										    unlabeled buttons to a screen reader. */}
										<span className="sr-only">Network fee</span>
										{(
											[
												['slow', speed?.slow],
												['medium', speed?.medium],
												['fast', speed?.fast]
											] as const
										).map(([k, v]) => (
											<button
												key={k}
												type="button"
												onClick={() => {
													closeReview()
													selectFee(k)
												}}
												className={cn(
													'alkanes-touch rounded px-1 py-0.5 text-xxs capitalize tabular-nums transition-colors',
													selected === k
														? 'bg-primary/15 font-semibold text-primary'
														: 'text-muted-foreground hover:text-foreground'
												)}
											>
												{k} {v ? Math.ceil(v) : '…'}
											</button>
										))}
									</span>
									{/* The REAL network fee, read off the built PSBT (the
									    collections v2-feed invoice pattern) — BTC / USD
									    visible, sats and the rate in the hover panel. */}
									<span className="min-w-0 truncate text-right tabular-nums">
										{prep.status === 'ready' ? (
											<HoverDetail
												heading="Network fee"
												align="right"
												detail={
													<React.Fragment>
														<span className="flex items-center justify-between gap-6 whitespace-nowrap">
															<span className="text-muted-foreground">Sats</span>
															<span className="tabular-nums">
																{prep.fee.toLocaleString('en-US')}
															</span>
														</span>
														<span className="flex items-center justify-between gap-6 whitespace-nowrap">
															<span className="text-muted-foreground">Rate</span>
															<span className="tabular-nums">{feeRate} sat/vB</span>
														</span>
													</React.Fragment>
												}
											>
												<span data-testid="alkanes-network-actual">
													{(Number(prep.fee) / 1e8).toFixed(8)} BTC
													{feeUsd !== null ? ` / ${formatUsd(feeUsd)}` : ''}
												</span>
											</HoverDetail>
										) : prep.status === 'building' ? (
											<span className="text-muted-foreground">…</span>
										) : (
											<span className="text-muted-foreground">—</span>
										)}
									</span>
								</div>
							</dl>
						</div>
					</div>

					<div className={footClassName}>
						<Button
							data-testid="alkanes-swap-submit"
							className={cn(
								'alkanes-touch h-12 w-full rounded-xl text-base font-bold transition-colors',
								// Explicit disabled surface: the default `disabled:opacity-50`
								// composited the label to 3.35:1 (measured). A muted surface
								// reads inert while keeping the label legible.
								'disabled:opacity-100 disabled:bg-secondary disabled:text-muted-foreground',
								ctaReady
									? highImpact
										? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
										: 'bg-primary text-primary-foreground shadow-lg shadow-primary/25 hover:bg-primary/90'
									: !marketDataDown && !watchMode && !loggedIn
									? // The connect door is live but it is not the money CTA —
									  // the accent stays reserved for a signable trade.
									  'bg-secondary text-foreground hover:bg-secondary/80'
									: undefined
							)}
							disabled={marketDataDown || watchMode || (loggedIn && !ctaReady)}
							onClick={() => {
								if (marketDataDown) {
									return
								}
								if (!loggedIn) {
									event.emit('command', { id: 'connect-wallet' })
									return
								}
								if (!ctaReady) {
									return
								}
								if (staged && (review === null || moved)) {
									// Step one (or a moved quote): freeze the review; the
									// invoice mounts with it and the CTA becomes the signer.
									openReview()
									return
								}
								// Direct trade: build, sign, broadcast — the wallet's
								// prompt is the confirmation step.
								signNow()
							}}
						>
							{submitting && <Loader2 className="mr-2 size-4 animate-spin" />}
							{buttonLabel}
						</Button>
						{mode !== 'wrapSwap' && (
							<p className="mt-2 text-center text-xxs leading-relaxed text-muted-foreground">
								{mode === 'unwrap'
									? 'Unwraps settle asynchronously — BTC arrives after the SUBFROST federation processes the transaction.'
									: mode === 'wrap'
									? 'frBTC arrives after one confirmation — 1:1 with BTC, spendable on any buy.'
									: mode === 'curve'
									? 'Self-custody — fills on-chain in the SUBFROST frBTC/frUSD curve pool.'
									: 'Self-custody — swaps execute on-chain via the SUBFROST AMM.'}
							</p>
						)}
					</div>
				</React.Fragment>
			)}
		</section>
	)
}
